/* eslint-disable no-unused-vars */
// @flow
import PropTypes from 'prop-types';
import React from 'react';
import type CompoundInput, { CompoundInputProps } from './CompoundInput';
import InputApi from './InputApi';
import { ReadOnlyMap } from './types';
import type { AbstractInput, ValidationProblem } from './types';

export type CommonInputProps<ValueType, ApiType: InputApi<ValueType> = any> = {
  /**
   * The className prop allows input components to be extendable by
   * styled-components using the `styled(Input)` syntax
   */
  className: string,

  /**
   * Called every time the input value changes, even if the changes are not
   * committed. (E.g. for text inputs, fires on every key-press, not just on
   * blur).
   */
  onChange?: (newValue: ValueType, api: ApiType) => void,
  /**
   * Called when an input has changed, and that change is "final". (E.g. for
   * text inputs, fires on blur, if the text content has actually changed).
   */
  onChangeCommitted?: (newValue: ValueType, api: ApiType) => void,

  /**
   * Called when a new value prop is received (either in this input or the
   * Form or RepeatingInput it gets values from) while the component has
   * unflushed changes (see `hasUnflushedChanges`). By default, these values
   * are discarded, and a warning is sent to console. Use this prop to suppress
   * that warning, and return `true` if you want to allow the new value to
   * allow the current user-entered value, or `false` to silently ignore it.
   */
  onValueReceivedWhileChanged?: (newReceivedValue: ValueType, api: ApiType) => boolean,

  /**
   * Called whenever the form is to validate itself. Add any custom
   * validation logic here (using `api.addValidationProblem()`)
   */
  onValidate?: (api: ApiType) => void,
  /**
   * Called whenever the number of validation problem changes for any reason.
   * Use this call back to render validation problems next to the field.
   */
  onValidationProblems?: (api: ApiType) => void,

  /**
   * Customize validation problem messages. Each key is a validation problem
   * name, and each value the custom message for that problem.
   */
  validationMessages?: { [name: string]: string },

  /**
   * A built-in validation available to ALL inputs. Adds a validation
   * problem named `REQUIRED` if this field has no value.
   */
  required?: boolean,

  /**
   * The value the input is bound to. Should not be used if this input is
   * inside a compound input.
   *
   * This prop is mutually exclusive with `name`.
   */
  value?: ValueType,
  /**
   * For use only with inputs inside a compound input, the name maps the input
   * to a key in the compound input's value.
   *
   * This prop is mutually exclusive with `value`.
   */
  name?: any,
};

export type InputContextType = {
  parent: ?CompoundInput<any, any, any>,
};

export default class InputBase<
    ValueType,
    InputPropTypes: CommonInputProps<ValueType> = CommonInputProps<ValueType>,
    StateType = { [string]: * },
  >
  extends React.Component<InputPropTypes, StateType>
  implements AbstractInput<ValueType>
{
  /**
   * Only used as a value of last resort by CompoundInputs that will get their
   * value from their children.
   */
  static +blankReceivedValue: ?ValueType;

  static contextTypes = {
    // actually CompoundInput but whatever
    parent: PropTypes.instanceOf(InputBase),
  };

  static defaultProps = {
    className: '',
    onValueReceivedWhileChanged(value: ValueType, api: InputApi<ValueType>) {},
  };

  constructor(props: InputPropTypes, context: InputContextType) {
    super(props, context);
    this._validationProblems = [];
    if (this.parent) {
      // eslint-disable-next-line prefer-destructuring
      const { parent } = this;
      parent.registerInput(this);
      if ('name' in props) {
        if ('value' in props) {
          // We have both a name and a value, it must be we are providing a
          // value to our parent.
          const value = this.cleanValue(this.receivedValue);
          this._currentValue = value;
          this._committedValue = value;

          // $FlowFixMe - this.parent refinement not catching
          this.parent.initializeValueFromChild(this.name, value);
        } else {
          this._currentValue = parent.getCurrentValueElement(props.name);
          this._committedValue = parent.getCommittedValueElement(props.name);
        }
      }
    } else {
      // No parent, we must be the root
      const value = this.cleanValue(this.receivedValue);
      this._currentValue = value;
      this._committedValue = value;
    }
    this.api = new InputApi(this);
  }

  UNSAFE_componentWillReceiveProps(props: InputPropTypes) {
    if ('value' in props) {
      // $FlowFixMe - props.value should have been refined by `in`
      this.didReceiveNewValue(props.value);
    }

    if (this.parent && props.name && this.props.name !== props.name) {
      this.parent.renameRegisteredInput(this.name, props.name);
    }
  }

  componentWillUnmount() {
    if (this.parent) {
      this.parent.unregisterInput(this);
    }
  }

  // region Input Interface (getters/setters)

  get receivedValue(): ValueType {
    if ('value' in this.props) {
      // $FlowFixMe - props.value should have been refined by `in`
      return this.cleanValue(this.props.value);
    }
    if (this.parent) {
      return this.parent.getReceivedValueElement(this.name);
    }
    if (this.constructor.blankReceivedValue) {
      return this.constructor.blankReceivedValue;
    }
    throw new Error(
      `Can't get received value for ${
        this.constructor.displayName || this.constructor.name
      } component as it has neither value nor parent`,
    );
  }

  get sourceValue(): ValueType {
    if (this.isFlushed()) {
      return this.committedValue;
    }
    return this.receivedValue;
  }

  get parent(): ?CompoundInput<any, any, any> {
    return this.context.parent;
  }

  get name(): any {
    if (this.props.name == null) {
      throw new Error(
        `${this.constructor.displayName || this.constructor.name} component lacks a` +
          ' name. (If you are trying to check if this component has a name,' +
          ' check this.parent instead - components with a parent MUST have a name.)',
      );
    }
    return this.props.name;
  }

  get currentValue(): ValueType {
    return this._currentValue;
  }

  get committedValue(): ValueType {
    return this._committedValue;
  }

  get validationProblems(): Array<ValidationProblem<any>> {
    return this._validationProblems;
  }

  set currentValue(value: ValueType) {
    this._currentValue = this.cleanValue(value);
  }

  set committedValue(value: ValueType) {
    this.currentValue = value;
    this._committedValue = this.currentValue;
    this._resetValidations();
  }

  +api: InputApi<ValueType>;
  +inputs: ?ReadOnlyMap<any, InputBase<any, CommonInputProps<any>>>;
  context: InputContextType = this.context;
  _validationProblems: Array<ValidationProblem<any>> = [];

  _committedValue: ValueType;
  _currentValue: ValueType;
  _validationProblemsBatch: ?Array<ValidationProblem<any>>;
  _notifyValidationProblemsAfterBatch: boolean;
  // Set to a unique Symbol to begin with so no value can accidentally
  // equal it.
  _flushedValue: ValueType | Symbol = Symbol('UnflushedValue');

  // region Input Interface (methods)

  flush() {
    this._flushedValue = this.currentValue;
    this._resetValidations();
  }

  isFlushed(): boolean {
    return this._flushedValue === this.currentValue;
  }

  reset() {
    this.committedValue = this.sourceValue;
  }

  isValid(): boolean {
    return this.validationProblems.length === 0;
  }

  validate(batchingCallback: ?() => void): boolean {
    // Do this instead of _resetValidations() to avoid a likely
    // superfluous call to onValidationProblems
    this._notifyValidationProblemsAfterBatch = this._validationProblems.length > 0;
    this._validationProblems = [];
    this._validationProblemsBatch = [];
    const batch = this._validationProblemsBatch;
    if (typeof batchingCallback === 'function') {
      batchingCallback();
    }
    this.runBuiltinValidations();
    if (typeof this.props.onValidate === 'function') {
      this.props.onValidate(this.api);
    }
    this._validationProblemsBatch = undefined;
    this.addValidationProblem(...batch);
    if (this._notifyValidationProblemsAfterBatch) {
      this._notifyValidationProblemsAfterBatch = false;
      this.validationProblemsDidChange();
    }
    return this.isValid();
  }

  addValidationProblem(...problems: Array<ValidationProblem<ValueType>>) {
    if (problems.length === 0) {
      return;
    }
    if (this._validationProblemsBatch) {
      this._validationProblemsBatch.push(...problems);
      return;
    }

    this._validationProblems.push(...this.overrideValidationProblemMessages(problems));

    this.validationProblemsDidChange();
  }

  validationProblemsDidChange() {
    if (this._validationProblemsBatch) {
      this._notifyValidationProblemsAfterBatch = true;
      return;
    }
    if (typeof this.props.onValidationProblems === 'function') {
      this.props.onValidationProblems(this.api);
    }
    if (this.parent) {
      this.parent.validationProblemsDidChange();
    }
  }

  hasUnflushedChanges(): boolean {
    if (this.isFlushed()) {
      return false;
    }
    return !this.areValuesEqual(this.currentValue, this.receivedValue);
  }

  // endregion

  /**
   * Used by required
   */
  hasValue(): boolean {
    return !!this.committedValue;
  }

  overrideValidationProblemMessages(
    problems: Array<ValidationProblem<any>>,
  ): Array<ValidationProblem<any>> {
    const messageOverrides = {};
    if (this.parent && typeof this.parent.props.validationMessages === 'object') {
      Object.assign(messageOverrides, this.parent.props.validationMessages);
    }
    if (typeof this.props.validationMessages === 'object') {
      Object.assign(messageOverrides, this.props.validationMessages);
    }
    if (Object.keys(messageOverrides).length > 0) {
      return problems.map(problem => {
        if (problem.name in messageOverrides) {
          return {
            ...problem,
            message: messageOverrides[problem.name],
          };
        }
        return problem;
      });
    }
    return problems;
  }

  runBuiltinValidations() {
    if (this.props.required && !this.hasValue()) {
      this.addValidationProblem({
        name: 'REQUIRED',
        message: 'This field is required',
        value: this.committedValue,
        inputName: [],
      });
    }
  }

  _resetValidations() {
    const hadValidationProblems = this.validationProblems.length > 0;
    this._validationProblems = [];
    if (hadValidationProblems) {
      this.validationProblemsDidChange();
    }
  }

  canReceiveNewValue(value: ValueType) {
    const newValue = this.cleanValue(value);
    if (this.areValuesEqual(newValue, this.currentValue)) {
      return false;
    }
    if (this.hasUnflushedChanges()) {
      if (typeof this.props.onValueReceivedWhileChanged === 'function') {
        return this.props.onValueReceivedWhileChanged(newValue, this.api);
      }
      return false;
    }
    return true;
  }

  didReceiveNewValue(value: ValueType, bypassOverwriteCheck: boolean = false) {
    const newValue = this.cleanValue(value);
    if (bypassOverwriteCheck || this.canReceiveNewValue(value)) {
      this.committedValue = newValue;
    }
  }

  cleanValue(value: ValueType): ValueType {
    return value;
  }

  notifyInputDidChange(value: ValueType) {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(value, this.api);
    }
    if (this.parent) {
      this.parent.childInputDidChange(this.name, value);
    }
  }

  notifyInputDidCommitChange(value: ValueType) {
    if (typeof this.props.onChangeCommitted === 'function') {
      this.props.onChangeCommitted(value, this.api);
    }
    if (this.parent) {
      this.parent.childInputDidCommitChange(this.name, value);
    }
  }

  areValuesEqual(a: ValueType, b: ValueType) {
    return a === b;
  }
}
